// react
import styled from 'styled-components';

// components
import {
  Button,
  CarouselResponsive,
  Container,
  Divider,
  ProgressCircle,
  Title,
} from '@/components/atoms';
import { ProfileSummary } from '@/components/molecules';
import {
  SuggestedArticles,
  SuggestedArticlesProps,
} from '@/components/organisms';

// models
import {
  Account,
  ActionPlanCategory as ActionPlanCategoryType,
  ActionPlanItem,
} from '@karehero/models';

// hooks
import { useWindowSizeBreakpoints } from '@/hooks';

/**
 * Dashboard is the page for the care profile.
 */
export const Dashboard = ({
  suggestedArticles,
  account,
  actionPlanCategories,
  showIntercom,
}: DashboardProps) => {
  // hooks
  const { isMd } = useWindowSizeBreakpoints();
  return (
    <Wrapper>
      <Container>
        <Header>
          <Title size='sm' isNoMargin>
            Welcome, {`${account?.firstName}`}
          </Title>
          <DashboardHeader>
            <ProfileSummary
              fallback=''
              name='Ready to help'
              role='Your Care Expert'
              size='sm'
              imgSrc='/images/care-expert.jpg'
            />
            <Button
              ariaLabel='chat'
              size='sm'
              variant='primary'
              iconLeft='message'
              onClick={showIntercom}
            >
              Chat
            </Button>
          </DashboardHeader>
        </Header>
      </Container>

      <Divider isOr={false} />

      <Container>
        <Body>
          <CarouselResponsive
            perView={isMd ? 2 : 1}
            items={[
              <ProgressCircle
                title='Find Care'
                description='KareHero finds your care'
                to='/action-plan/category/find-care'
                color='orange'
                limit={
                  actionPlanCategories?.find(
                    (category: ActionPlanCategoryType) =>
                      category.id === 'find-care',
                  )?.totalItems || 0
                }
                progress={
                  actionPlanCategories?.find(
                    (category: ActionPlanCategoryType) =>
                      category.id === 'find-care',
                  )?.completedItems || 0
                }
              />,
              <ProgressCircle
                title='Cover Costs'
                description='Karehero helps cover costs'
                to='/action-plan/category/cover-costs'
                color='green'
                limit={
                  actionPlanCategories?.find(
                    (category: ActionPlanCategoryType) =>
                      category.id === 'cover-costs',
                  )?.totalItems || 0
                }
                progress={
                  actionPlanCategories?.find(
                    (category: ActionPlanCategoryType) =>
                      category.id === 'cover-costs',
                  )?.completedItems || 0
                }
              />,
              <ProgressCircle
                title='Power of Attorney'
                description='Get covered'
                to='/action-plan/set-up-power-of-attorney'
                color='yellow'
                limit={1}
                progress={
                  actionPlanCategories
                    ?.find(
                      (category: ActionPlanCategoryType) =>
                        category.id === 'getting-started',
                    )
                    ?.actionPlanItems?.find(
                      (item: ActionPlanItem) =>
                        item.id === 'set-up-power-of-attorney',
                    )?.isComplete
                    ? 1
                    : 0
                }
              />,
            ]}
          />
          <SuggestedArticles {...suggestedArticles} />
        </Body>
      </Container>
    </Wrapper>
  );
};

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${(props) => props.theme.spacing[24]};
  padding-bottom: ${(props) => props.theme.spacing[48]};
`;

const Body = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${(props) => props.theme.spacing[48]};
`;

const Header = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${(props) => props.theme.spacing[16]};
  position: relative;
  padding-top: ${(props) => props.theme.spacing[24]};
`;

const DashboardHeader = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  gap: ${(props) => props.theme.spacing[16]};
`;

interface DashboardProps {
  suggestedArticles?: SuggestedArticlesProps;
  actionPlanItems: ActionPlanItem[];
  allActionPlanItems: ActionPlanItem[];
  onActionPlanItemDismiss: (actionPlanItemId: string) => void;
  account: Account;
  actionPlanCategories?: ActionPlanCategoryType[];
  showIntercom: () => void;
}

Dashboard.displayName = 'Dashboard';

export default Dashboard;
